import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { Toaster } from "sonner";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import RequestStaffPublic from "@/pages/RequestStaffPublic";
import JoinPublic from "@/pages/JoinPublic";
import OfferTokenPage from "@/pages/OfferTokenPage";

import {
  PrivacyPolicy,
  TermsAndConditions,
  CookiePolicy,
  AccessibilityStatement,
} from "@/pages/legal/LegalPages";

import FacilityLayout from "@/pages/facility/FacilityLayout";
import FacilityDashboard from "@/pages/facility/FacilityDashboard";
import FacilityRequests from "@/pages/facility/FacilityRequests";
import FacilityNewRequest from "@/pages/facility/FacilityNewRequest";
import FacilityProposals from "@/pages/facility/FacilityProposals";
import FacilityAssignments from "@/pages/facility/FacilityAssignments";
import FacilityInvoices from "@/pages/facility/FacilityInvoices";
import FacilityIncidents from "@/pages/facility/FacilityIncidents";

import DispatcherLayout from "@/pages/dispatcher/DispatcherLayout";
import DispatcherDashboard from "@/pages/dispatcher/DispatcherDashboard";
import DispatcherRequests from "@/pages/dispatcher/DispatcherRequests";
import DispatcherRequestDetail from "@/pages/dispatcher/DispatcherRequestDetail";
import DispatcherBoard from "@/pages/dispatcher/DispatcherBoard";
import DispatcherProfessionals from "@/pages/dispatcher/DispatcherProfessionals";
import DispatcherProfessionalDetail from "@/pages/dispatcher/DispatcherProfessionalDetail";
import DispatcherMap from "@/pages/dispatcher/DispatcherMap";
import DispatcherInvoices from "@/pages/dispatcher/DispatcherInvoices";
import DispatcherIncidents from "@/pages/dispatcher/DispatcherIncidents";
import DispatcherAuditLog from "@/pages/dispatcher/DispatcherAuditLog";
import DispatcherServices from "@/pages/dispatcher/DispatcherServices";

import ProfessionalLayout from "@/pages/professional/ProfessionalLayout";
import ProfessionalHome from "@/pages/professional/ProfessionalHome";
import ProfessionalOffers from "@/pages/professional/ProfessionalOffers";
import ProfessionalShifts from "@/pages/professional/ProfessionalShifts";
import ProfessionalTimesheets from "@/pages/professional/ProfessionalTimesheets";
import ProfessionalProfile from "@/pages/professional/ProfessionalProfile";

function ProtectedRoute({ roles, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen text-brand-navy">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function RoleRedirect() {
  const { user } = useAuth();
  if (!user) return <Landing />;
  if (user.role === "facility") return <Navigate to="/facility" replace />;
  if (user.role === "professional") return <Navigate to="/pro" replace />;
  if (user.role === "dispatcher" || user.role === "super_admin")
    return <Navigate to="/dispatch" replace />;
  return <Landing />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/request-staff" element={<RequestStaffPublic />} />
            <Route path="/join" element={<JoinPublic />} />
            <Route path="/dashboard" element={<RoleRedirect />} />
            <Route path="/offer/:token" element={<OfferTokenPage />} />

            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/cookie-policy" element={<CookiePolicy />} />
            <Route path="/accessibility-statement" element={<AccessibilityStatement />} />

            <Route
              path="/facility"
              element={
                <ProtectedRoute roles={["facility"]}>
                  <FacilityLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<FacilityDashboard />} />
              <Route path="requests" element={<FacilityRequests />} />
              <Route path="requests/new" element={<FacilityNewRequest />} />
              <Route path="proposals" element={<FacilityProposals />} />
              <Route path="assignments" element={<FacilityAssignments />} />
              <Route path="invoices" element={<FacilityInvoices />} />
              <Route path="incidents" element={<FacilityIncidents />} />
            </Route>

            <Route
              path="/dispatch"
              element={
                <ProtectedRoute roles={["dispatcher", "super_admin"]}>
                  <DispatcherLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DispatcherDashboard />} />
              <Route path="requests" element={<DispatcherRequests />} />
              <Route path="requests/:id" element={<DispatcherRequestDetail />} />
              <Route path="board" element={<DispatcherBoard />} />
              <Route path="professionals" element={<DispatcherProfessionals />} />
              <Route path="professionals/:id" element={<DispatcherProfessionalDetail />} />
              <Route path="map" element={<DispatcherMap />} />
              <Route path="invoices" element={<DispatcherInvoices />} />
              <Route path="incidents" element={<DispatcherIncidents />} />
              <Route path="services" element={<DispatcherServices />} />
              <Route path="audit" element={<DispatcherAuditLog />} />
            </Route>

            <Route
              path="/pro"
              element={
                <ProtectedRoute roles={["professional"]}>
                  <ProfessionalLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<ProfessionalHome />} />
              <Route path="offers" element={<ProfessionalOffers />} />
              <Route path="shifts" element={<ProfessionalShifts />} />
              <Route path="timesheets" element={<ProfessionalTimesheets />} />
              <Route path="profile" element={<ProfessionalProfile />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
